import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS, getAuthMeEndpoint } from '../config/api';
import { apiFetch, isTokenExpired, AUTH_EXPIRED_EVENT } from '../utils/api';
import {
  decodeJwtPayload,
  getUserIdFromJwtPayload,
  isJwtExpiredPayload,
  normalizeUrlAccessToken,
} from '../utils/urlTokenAuth';
import { normalizeUserProfileFromApi } from '../utils/userProfile';
import { getSendOtpCooldownRemainingSec, recordOtpVerifySuccess, resetOtpStreakForNewOtp } from '../utils/otpLockout';

const LOGIN_API = API_ENDPOINTS.authLogin;
const SEND_OTP_API = API_ENDPOINTS.authSendOtp;
const VERIFY_MOBILE_OTP_API = API_ENDPOINTS.authVerifyMobileOtp;
const CREATE_ACCOUNT_API = API_ENDPOINTS.authCreateAccount;
const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'auth_user';

const AuthContext = createContext({
  user: null,
  token: null,
  login: async () => {},
  logout: () => {},
  sendOtp: async () => {},
  verifyOtp: async () => {},
  createAccount: async () => {},
  updateUser: () => {},
  loginFromUrlToken: async () => ({ ok: false }),
  isAuthenticated: false,
});

function readStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getInitialToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(getInitialToken);
  const [user, setUser] = useState(readStoredUser);
  /** Bumps periodically so `isTokenExpired()` can flip `isAuthenticated` without waiting for an API call. */
  const [sessionPulse, setSessionPulse] = useState(0);

  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    const storedUser = readStoredUser();
    setToken(stored);
    setUser(storedUser);
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setSessionPulse((p) => p + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const onFocus = () => setSessionPulse((p) => p + 1);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const login = useCallback(async (username, password) => {
    const response = await fetch(LOGIN_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });

    const json = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(json?.message || 'Login failed');
    }

    const accessToken = json?.access_token || json?.token || json?.accessToken || '';
    const refreshToken = json?.refresh_token || json?.refreshToken || '';
    const userProfile = json?.user || json?.profile || null;

    if (!accessToken) {
      throw new Error('Login succeeded but token was missing');
    }

    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }

    if (userProfile) {
      localStorage.setItem(USER_KEY, JSON.stringify(userProfile));
    } else {
      localStorage.removeItem(USER_KEY);
    }

    setToken(accessToken);
    setUser(userProfile);

    return { token: accessToken, refreshToken, user: userProfile };
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  /** Keep React auth state in sync when API clears the session (401) or cart uses raw fetch. */
  useEffect(() => {
    const onAuthExpired = () => logout();
    window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
  }, [logout]);

  /** Drop expired sessions so UI and headers match (Sign in + no stale Bearer). */
  useEffect(() => {
    if (!token) return;
    if (!isTokenExpired()) return;
    logout();
  }, [token, sessionPulse, logout]);

  const updateUser = useCallback((userProfile) => {
    if (!userProfile) return;
    const merged = { ...readStoredUser(), ...userProfile };
    localStorage.setItem(USER_KEY, JSON.stringify(merged));
    setUser(merged);
  }, []);

  const saveAuth = (accessToken, userProfile, refreshToken = '') => {
    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
    if (userProfile) {
      localStorage.setItem(USER_KEY, JSON.stringify(userProfile));
    } else {
      localStorage.removeItem(USER_KEY);
    }
    setToken(accessToken);
    setUser(userProfile);
  };

  const sendOtp = useCallback(async (mobileNumber) => {
    const waitSec = getSendOtpCooldownRemainingSec(mobileNumber);
    if (waitSec > 0) {
      const err = new Error('OTP_COOLDOWN');
      err.code = 'OTP_COOLDOWN';
      err.seconds = waitSec;
      throw err;
    }
    const response = await apiFetch(SEND_OTP_API, {
      method: 'POST',
      skipAuth: true,
      body: JSON.stringify({ mobileNumber: String(mobileNumber).trim() }),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(json?.message || 'Failed to send OTP');
    }
    resetOtpStreakForNewOtp(mobileNumber);
    return json;
  }, []);

  const verifyOtp = useCallback(async (mobileNumber, otp) => {
    const response = await apiFetch(VERIFY_MOBILE_OTP_API, {
      method: 'POST',
      skipAuth: true,
      body: JSON.stringify({
        mobileNumber: String(mobileNumber).trim(),
        otp: String(otp).trim(),
      }),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(json?.message || 'OTP verification failed');
    }
    recordOtpVerifySuccess(mobileNumber);
    const isNewUser = json?.isNewUser === true || json?.is_new_user === true;
    const accessToken =
      json?.token ||
      json?.access_token ||
      json?.accessToken ||
      json?.data?.token ||
      json?.data?.access_token ||
      json?.data?.accessToken ||
      '';
    const userProfile = json?.user || json?.profile || json?.data?.user || json?.data?.profile || null;
    if (!isNewUser && accessToken) {
      saveAuth(accessToken, userProfile);
    }
    return { isNewUser, token: accessToken, user: userProfile };
  }, []);

  /**
   * Silent login from WhatsApp / CRM deep link (?tkn=).
   * Uses GET /api/auth/me with the URL token, saves user to localStorage, sets session logged in.
   * @returns {{ ok: boolean, user?: object|null, partial?: boolean, reason?: string }}
   */
  const loginFromUrlToken = useCallback(async (rawToken) => {
    const accessToken = normalizeUrlAccessToken(rawToken);
    if (!accessToken) {
      return { ok: false, reason: 'empty' };
    }

    const payload = decodeJwtPayload(accessToken);
    if (!payload) {
      return { ok: false, reason: 'invalid_jwt' };
    }
    if (isJwtExpiredPayload(payload)) {
      return { ok: false, reason: 'expired' };
    }

    const jwtUserId = getUserIdFromJwtPayload(payload);
    const authPrefix = (process.env.REACT_APP_AUTH_PREFIX || 'Bearer').trim();
    const authHeaders = {
      'Content-Type': 'application/json',
      Authorization: `${authPrefix} ${accessToken}`.trim(),
    };

    let userProfile = null;

    try {
      const res = await fetch(getAuthMeEndpoint(), { headers: authHeaders });
      if (res.ok) {
        const json = await res.json().catch(() => ({}));
        const data = json?.data ?? json;
        const meUserId =
          data?.userId ?? data?.user_id ?? data?.id ?? jwtUserId ?? '';
        if (meUserId || data?.name || data?.email || data?.mobileNumber) {
          userProfile = normalizeUserProfileFromApi(data, {
            id: meUserId,
            userId: meUserId,
            email: payload.email || data?.email || '',
          });
        }
      }
    } catch {
      /* fall through */
    }

    if (!userProfile && jwtUserId) {
      try {
        const res = await fetch(API_ENDPOINTS.userById(jwtUserId), { headers: authHeaders });
        if (res.ok) {
          const json = await res.json().catch(() => ({}));
          const data = json?.data ?? json;
          if (data && (data.id || data.userId || data.user_id)) {
            userProfile = normalizeUserProfileFromApi(data, {
              id: jwtUserId,
              userId: jwtUserId,
              email: payload.email || '',
            });
          }
        }
      } catch {
        /* fall through */
      }
    }

    if (!userProfile) {
      if (!jwtUserId) {
        return { ok: false, reason: 'auth_me_failed' };
      }
      userProfile = normalizeUserProfileFromApi(
        { id: jwtUserId, userId: jwtUserId, email: payload.email || '' },
        { id: jwtUserId, userId: jwtUserId, email: payload.email || '' }
      );
      saveAuth(accessToken, userProfile);
      return { ok: true, user: userProfile, partial: true };
    }

    saveAuth(accessToken, userProfile);
    return { ok: true, user: userProfile, partial: false };
  }, []);

  const createAccount = useCallback(async (mobileNumber, name, email, password) => {
    const response = await apiFetch(CREATE_ACCOUNT_API, {
      method: 'POST',
      skipAuth: true,
      body: JSON.stringify({
        mobileNumber: String(mobileNumber).trim(),
        name: String(name).trim(),
        email: String(email).trim(),
        password: String(password),
      }),
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(json?.message || 'Failed to create account');
    }
    const accessToken =
      json?.token ||
      json?.access_token ||
      json?.accessToken ||
      json?.data?.token ||
      json?.data?.access_token ||
      '';
    const userProfile = json?.user || json?.profile || json?.data?.user || json?.data?.profile || null;
    if (!accessToken) {
      throw new Error('Account created but token was missing');
    }
    saveAuth(accessToken, userProfile);
    return { token: accessToken, user: userProfile };
  }, []);

  const isAuthenticated = Boolean(token) && !isTokenExpired();

  const value = useMemo(
    () => ({
      user,
      token,
      login,
      logout,
      sendOtp,
      verifyOtp,
      createAccount,
      updateUser,
      loginFromUrlToken,
      isAuthenticated,
    }),
    [
      token,
      user,
      isAuthenticated,
      login,
      logout,
      sendOtp,
      verifyOtp,
      createAccount,
      updateUser,
      loginFromUrlToken,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
